import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import apiClient from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const CreateAccountContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CreateAccountCard = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 480px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
    border-radius: 20px;
    max-width: 100%;
  }
`;

const Title = styled.h1`
  color: #000000;
  margin-bottom: 2rem;
  font-size: 2rem;
  text-align: center;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.75rem;
  }
`;

const Notice = styled.div`
  background: #e7f5ff;
  border: 1px solid #339af0;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;
  text-align: left;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const NoticeText = styled.div`
  font-size: 0.875rem;
  color: #1971c2;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  text-align: left;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const SuccessMessage = styled.div`
  color: #0f5132;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #d1e7dd;
  border: 1px solid #badbcc;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const ConsentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ConsentLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ConsentBox = styled.div`
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  max-height: 120px;
  overflow-y: auto;
  background: #f8f9fa;
  margin-bottom: 0.75rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #e9ecef;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #6c757d;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    max-height: 100px;
  }
`;

const ConsentText = styled.div`
  font-size: 0.8125rem;
  color: #495057;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9375rem;
  color: #343a40;
  cursor: pointer;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 1rem;
  text-decoration: underline;
  
  &:hover {
    color: #000000;
  }
`;

const MemberInfo = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  text-align: left;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 0.875rem;
  color: #000000;
  font-weight: 500;
`;

const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState<'verify' | 'create'>('verify');
  const [verifyData, setVerifyData] = useState({
    name: '',
    phone: '',
    birthYear: ''
  });
  const [accountData, setAccountData] = useState({
    user_id: '',
    password: '',
    passwordConfirm: ''
  });
  const [memberInfo, setMemberInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    const limited = numbers.slice(0, 11);
    
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 7) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
    }
  };

  const handleVerifyInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    if (field === 'birthYear') {
      const numbers = value.replace(/[^\d]/g, '');
      formattedValue = numbers.slice(0, 6);
    }
    
    setVerifyData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
    setError('');
  };

  const handleAccountInputChange = (field: string, value: string) => {
    setAccountData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verifyData.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    
    if (!verifyData.phone.trim()) {
      setError('전화번호를 입력해주세요.');
      return;
    }
    
    if (!verifyData.birthYear.trim()) {
      setError('생년월일을 입력해주세요.');
      return;
    }
    
    if (verifyData.birthYear.length !== 6) {
      setError('생년월일은 6자리로 입력해주세요.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.post('/auth/verify-member', {
        name: verifyData.name,
        phone: verifyData.phone,
        birth_year: verifyData.birthYear
      });

      if (response.data.success) {
        setMemberInfo(response.data.data.member);
        setStep('create');
      }
    } catch (err: any) {
      console.error('회원 확인 오류:', err);
      const errorMessage = err.response?.data?.detail || '승인된 회원을 찾을 수 없습니다. 가입신청 후 관리자 승인을 기다려주세요.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accountData.user_id.trim()) {
      setError('아이디를 입력해주세요.');
      return;
    }
    
    if (accountData.user_id.length < 4) {
      setError('아이디는 4자 이상이어야 합니다.');
      return;
    }
    
    if (!accountData.password) {
      setError('비밀번호를 입력해주세요.');
      return;
    }
    
    if (accountData.password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    
    if (accountData.password !== accountData.passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.post('/auth/create-account', {
        user_no: memberInfo.user_no,
        user_id: accountData.user_id,
        password: accountData.password
      });

      if (response.data.success) {
        setSuccess('계정이 생성되었습니다! 로그인 페이지로 이동합니다.');
        
        // 토큰과 사용자 정보 저장
        login(response.data.data.access_token, response.data.data.user);
        
        // 2초 후 관리자/회원 페이지로 이동
        setTimeout(() => {
          if (response.data.data.user.is_admin) {
            navigate('/admin');
          } else {
            navigate('/member');
          }
        }, 2000);
      }
    } catch (err: any) {
      console.error('계정 생성 오류:', err);
      const errorMessage = err.response?.data?.detail || '계정 생성에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 1단계: 회원 확인
  if (step === 'verify') {
    return (
      <CreateAccountContainer>
        <CreateAccountCard>
          <Title>회원가입</Title>
          
          <Notice>
            <NoticeText>
              가입신청 후 승인된 회원만 계정을 만들 수 있습니다.
              <br />
              회원 정보를 입력해주세요.
            </NoticeText>
          </Notice>
          
          <Form onSubmit={handleVerifySubmit}>
            <Input
              label="이름"
              type="text"
              value={verifyData.name}
              onChange={(e) => handleVerifyInputChange('name', e.target.value)}
              placeholder="이름을 입력하세요"
              required
            />
            
            <Input
              label="전화번호"
              type="tel"
              value={verifyData.phone}
              onChange={(e) => handleVerifyInputChange('phone', e.target.value)}
              placeholder="010-0000-0000"
              required
            />
            
            <Input
              label="생년월일"
              type="text"
              value={verifyData.birthYear}
              onChange={(e) => handleVerifyInputChange('birthYear', e.target.value)}
              placeholder="6자리 (예: 231105)"
              required
            />
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              disabled={loading}
            >
              {loading ? '확인 중...' : '회원 확인'}
            </Button>
          </Form>
          
          <BackButton onClick={() => navigate('/')}>
            돌아가기
          </BackButton>
        </CreateAccountCard>
      </CreateAccountContainer>
    );
  }

  // 2단계: 계정 생성
  return (
    <CreateAccountContainer>
      <CreateAccountCard>
        <Title>계정 생성</Title>
        
        <MemberInfo>
          <InfoRow>
            <InfoLabel>이름</InfoLabel>
            <InfoValue>{verifyData.name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>전화번호</InfoLabel>
            <InfoValue>{verifyData.phone}</InfoValue>
          </InfoRow>
        </MemberInfo>
        
        <Form onSubmit={handleAccountSubmit}>
          <Input
            label="아이디"
            type="text"
            value={accountData.user_id}
            onChange={(e) => handleAccountInputChange('user_id', e.target.value)}
            placeholder="아이디 (4자 이상)"
            required
          />
          
          <Input
            label="비밀번호"
            type="password"
            value={accountData.password}
            onChange={(e) => handleAccountInputChange('password', e.target.value)}
            placeholder="비밀번호 (6자 이상)"
            required
          />
          
          <Input
            label="비밀번호 확인"
            type="password"
            value={accountData.passwordConfirm}
            onChange={(e) => handleAccountInputChange('passwordConfirm', e.target.value)}
            placeholder="비밀번호를 다시 입력하세요"
            required
          />
          
          
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={loading}
          >
            {loading ? '생성 중...' : '계정 생성'}
          </Button>
        </Form>
        
        <BackButton onClick={() => setStep('verify')}>
          이전 단계로
        </BackButton>
      </CreateAccountCard>
    </CreateAccountContainer>
  );
};

export default CreateAccount;

